import { api, qs } from '../client'
import type {
  DataExport,
  MutedWord,
  Page,
  Preferences,
  PreferencesPatch,
  ProfilePage,
  User,
  UserTab,
} from '../types'

interface UserResponse {
  user: User
}

export interface ProfilePatch {
  display_name?: string
  bio?: string
  location?: string
  website?: string
  avatar_media_id?: string | null
  banner_media_id?: string | null
  /** 保护我的帖子（D75）。取消保护时待处理的关注请求全部批准（D76） */
  is_private?: boolean
}

export const usersApi = {
  /** 数据导出（D85）：只走网页会话 */
  latestExport: () =>
    api.get<{ export: DataExport | null }>('/users/me/export').then((r) => r.export),
  requestExport: () => api.post<{ export: DataExport }>('/users/me/export').then((r) => r.export),

  get: (username: string) =>
    api.get<UserResponse>(`/users/${encodeURIComponent(username)}`).then((r) => r.user),

  updateMe: (payload: ProfilePatch) =>
    api.patch<UserResponse>('/users/me', payload).then((r) => r.user),

  /** 「帖子」tab 的第一页额外带 `pinned` */
  posts: (username: string, tab: UserTab, cursor?: string, limit = 20) =>
    api.get<ProfilePage>(
      `/users/${encodeURIComponent(username)}/posts${qs({ tab, cursor, limit })}`,
    ),

  followers: (username: string, cursor?: string, limit = 20) =>
    api.get<Page<User>>(`/users/${encodeURIComponent(username)}/followers${qs({ cursor, limit })}`),

  following: (username: string, cursor?: string, limit = 20) =>
    api.get<Page<User>>(`/users/${encodeURIComponent(username)}/following${qs({ cursor, limit })}`),

  follow: (username: string) =>
    api.post<UserResponse>(`/users/${encodeURIComponent(username)}/follow`).then((r) => r.user),

  unfollow: (username: string) =>
    api.delete<UserResponse>(`/users/${encodeURIComponent(username)}/follow`).then((r) => r.user),

  block: (username: string) =>
    api.post<UserResponse>(`/users/${encodeURIComponent(username)}/block`).then((r) => r.user),
  unblock: (username: string) =>
    api.delete<UserResponse>(`/users/${encodeURIComponent(username)}/block`).then((r) => r.user),

  mute: (username: string) =>
    api.post<UserResponse>(`/users/${encodeURIComponent(username)}/mute`).then((r) => r.user),
  unmute: (username: string) =>
    api.delete<UserResponse>(`/users/${encodeURIComponent(username)}/mute`).then((r) => r.user),

  // ---- 关注请求（D76）----
  followRequests: (cursor?: string, limit = 20) =>
    api.get<Page<User>>(`/users/me/follow-requests${qs({ cursor, limit })}`),
  approveFollowRequest: (username: string) =>
    api.post<{ count: number }>(
      `/users/me/follow-requests/${encodeURIComponent(username)}/approve`,
    ),
  rejectFollowRequest: (username: string) =>
    api.post<{ count: number }>(`/users/me/follow-requests/${encodeURIComponent(username)}/reject`),

  blocked: (cursor?: string, limit = 20) =>
    api.get<Page<User>>(`/users/me/blocks${qs({ cursor, limit })}`),
  muted: (cursor?: string, limit = 20) =>
    api.get<Page<User>>(`/users/me/mutes${qs({ cursor, limit })}`),

  suggestions: () => api.get<{ items: User[] }>('/users/suggestions').then((r) => r.items),

  // ---- 设置 ----

  /** 旧帖子里的 @旧名 不会跟着改，旧名也会立刻释放 */
  changeUsername: (username: string) =>
    api.patch<UserResponse>('/users/me/username', { username }).then((r) => r.user),

  preferences: () =>
    api.get<{ preferences: Preferences }>('/users/me/preferences').then((r) => r.preferences),
  updatePreferences: (patch: PreferencesPatch) =>
    api
      .patch<{ preferences: Preferences }>('/users/me/preferences', patch)
      .then((r) => r.preferences),

  mutedWords: () => api.get<{ items: MutedWord[] }>('/users/me/muted-words').then((r) => r.items),
  addMutedWord: (phrase: string) =>
    api.post<{ word: MutedWord }>('/users/me/muted-words', { phrase }).then((r) => r.word),
  removeMutedWord: (id: string) => api.delete<{ ok: boolean }>(`/users/me/muted-words/${id}`),

  updateAgent: (payload: {
    account_type?: 'human' | 'agent'
    model?: string
    purpose?: string
    homepage?: string
    public_key?: string
  }) => api.patch<UserResponse>('/users/me/agent', payload).then((r) => r.user),
}
